import { useEffect, useState } from 'react'
import { api, type Importbild, type OffeneGruppe } from '../api'
import { anzahl, datumMittel } from '../format'

/* Der tägliche Import.
 *
 * Abends liegen vier Dateien nebeneinander — Archiv, Quittungsreport,
 * Stornoreport, gelegentlich eine neue Artikelliste. Sie einzeln
 * hochzuladen wäre viermal dieselbe Handlung, also nimmt das Feld sie
 * alle auf einmal.
 *
 * Und das Ergebnis wird **je Datei** gezeigt, nicht als eine Zahl. Wer
 * vier Dateien schickt und „3 geladen" liest, weiß nicht, welche
 * fehlt — und schickt beim nächsten Mal alle vier noch einmal.
 */

const ARTEN = [
  { wert: 'FOOD', name: 'Speise' },
  { wert: 'BEVERAGE', name: 'Getränk' },
  { wert: 'OTHER', name: 'Sonstiges' },
]

const ZUSTAND: Record<string, string> = {
  GELADEN: 'geladen',
  DOPPELT: 'war schon da',
  UEBERSPRUNGEN: 'übersprungen',
  FEHLER: 'nicht angenommen',
}

/* Das Fenster für neue Warengruppen.
 *
 * Die **einzige** Stelle, an der das Haus an Warengruppen darf, und sie
 * ist mit Absicht schmal: ein Feld, und nur für Gruppen, die neu und
 * noch offen sind. Einmal gesetzt, verschwindet die Gruppe hier —
 * ändern kann sie danach nur der Betreiber.
 */
function OffeneGruppen({ gruppen, fertig }:
                       { gruppen: OffeneGruppe[]; fertig: () => void }) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)

  const setzen = async (name: string, art: string) => {
    setFehler(null); setLaeuft(name)
    try {
      await api.haus.warengruppeSetzen(name, art)
      fertig()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    } finally {
      setLaeuft(null)
    }
  }

  if (gruppen.length === 0) return null

  return (
    <div className="karte hervor">
      <h3>
        {gruppen.length === 1
          ? 'Eine neue Warengruppe'
          : `${anzahl(gruppen.length)} neue Warengruppen`}
      </h3>
      <p className="dezent">
        Aus den Artikeln ließ sich nicht ablesen, ob das Speisen oder
        Getränke sind. Solange das offen ist, fehlen diese Gruppen in der
        Aufteilung Speisen/Getränke — die Umsatzzahlen stimmen, die
        Aufteilung nicht.
      </p>
      <p className="dezent">
        <b>Einmal festlegen.</b> Danach steht es fest; wer es später
        ändern muss, wendet sich an den Betreiber.
      </p>

      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      <table>
        <thead>
          <tr>
            <th>Warengruppe</th><th>Steuerprofil</th>
            <th className="r">Artikel</th><th>Speise oder Getränk</th>
          </tr>
        </thead>
        <tbody>
          {gruppen.map((g) => (
            <tr key={g.schluessel}>
              <td><b>{g.name}</b></td>
              <td className="dezent">{g.steuerprofil ?? '—'}</td>
              <td className="r zahl">{anzahl(g.artikel)}</td>
              <td>
                <div className="knopfreihe">
                  {ARTEN.map((a) => (
                    <button key={a.wert} className="verweis"
                            disabled={laeuft === g.name}
                            onClick={() => void setzen(g.name, a.wert)}>
                      {a.name}
                    </button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="dezent">
        Das Steuerprofil beantwortet das übrigens <b>nicht</b>: Unter
        „vollrate 19 %" stehen elf Getränke — und Non Food.
      </p>
    </div>
  )
}

/* `nachImport` frischt den Kontext des Hauses auf — Zeitraum, Standorte,
 * neuester Geschäftstag. Ohne den Rückruf blieb die Auswertung nach dem
 * ersten Import bei „Es liegen noch keine Geschäftstage vor" stehen,
 * obwohl hier „geladen" stand: Der Kontext wurde nur beim Anmelden
 * geholt. Wer das sieht, lädt dieselben Dateien noch einmal hoch.
 */
export function Import({ nachImport }: { nachImport?: () => void } = {}) {
  const [dateien, setDateien] = useState<File[]>([])
  const [erg, setErg] = useState<Importbild | null>(null)
  const [offen, setOffen] = useState<OffeneGruppe[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const offeneLaden = () => {
    api.haus.offeneWarengruppen()
      .then((d) => setOffen(d.offen))
      .catch(() => undefined)
  }
  useEffect(offeneLaden, [])

  const senden = async () => {
    setFehler(null); setLaeuft(true)
    try {
      const a = await api.haus.importieren(dateien)
      setErg(a)
      setOffen(a.offene_warengruppen)
      setDateien([])
      // Auch wenn nur eine schon bekannte Datei dabei war: Sie erneuert
      // Verdichtung und Abgleich, und genau deshalb schickt man sie ein
      // zweites Mal.
      if (a.tage.length > 0) nachImport?.()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="backoffice">
      <div className="karte">
        <h3>Kassendaten einlesen</h3>
        <p className="dezent">
          Das Kassenarchiv (<span className="zahl">.zip</span>) — für einen
          Tag oder für einen ganzen Zeitraum, dann wird es in seine
          Geschäftstage zerlegt. Dazu die Berichte
          (<span className="zahl">.xls</span> oder
          <span className="zahl"> .csv</span>): Quittungen, Stornos,
          Transaktionen, Zahlungen. Und bei Bedarf die Artikelliste
          (<span className="zahl">.xlsx</span>).
          Dieselbe Datei zweimal zu schicken schadet nicht: Sie wird an
          ihrer Prüfsumme erkannt und nicht doppelt geschrieben —
          Verdichtung und Abgleich entstehen dabei neu.
        </p>

        <div className="importregel">
          <b>Ein Upload gehört zusammen</b>
          <p>
            Die Auswahlreihenfolge im Dateidialog ist gleichgültig. Der
            Import liest selbst zuerst die Artikelliste, dann die Archive
            und danach die Berichte.
          </p>
          <ul>
            <li>
              Ein Geschäftstag gehört in <b>einen Upload</b>: Archiv und
              Berichte desselben Tages zusammen auswählen.
            </li>
            <li>
              Bei einem <b>neuen Standort</b> gehört die Artikelliste in
              den ersten Upload. Nach den Archiven wirkt sie nicht
              rückwirkend.
            </li>
          </ul>
        </div>

        <input type="file" multiple
               accept=".zip,.xls,.xlsx,.csv"
               onChange={(e) => {
                 setDateien(Array.from(e.target.files ?? []))
                 setErg(null)
               }} />

        {dateien.length > 0 && (
          <ul className="dateiliste">
            {dateien.map((d) => (
              <li key={d.name}>
                {d.name}
                <span className="dezent zahl">
                  {' '}· {Math.round(d.size / 1024)} KB
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="knopfreihe">
          <button disabled={dateien.length === 0 || laeuft}
                  onClick={() => void senden()}>
            {laeuft ? 'Wird eingelesen …' : 'Einlesen'}
          </button>
        </div>

        {laeuft && (
          <p className="dezent">
            Ein Archiv braucht ein paar Sekunden — es wird nicht nur
            gespeichert, sondern zerlegt, den Rollen zugeordnet und
            verdichtet.
          </p>
        )}

        {fehler && <div className="fehler" role="alert">{fehler}</div>}

        {erg && (
          <div className="ladeergebnis">
            <b>
              {anzahl(erg.zahlen.geladen)} eingelesen
              {erg.zahlen.doppelt > 0
                && `, ${anzahl(erg.zahlen.doppelt)} schon bekannt`}
              {erg.zahlen.fehler > 0
                && `, ${anzahl(erg.zahlen.fehler)} nicht angenommen`}
            </b>

            <table>
              <thead>
                <tr><th>Datei</th><th>Zustand</th><th>Anmerkung</th></tr>
              </thead>
              <tbody>
                {erg.dateien.map((d) => (
                  <tr key={d.name}
                      className={d.zustand === 'FEHLER' ? 'rot' : undefined}>
                    <td>{d.name}</td>
                    <td>{ZUSTAND[d.zustand] ?? d.zustand}</td>
                    <td className="dezent">
                      <div>{d.meldung}</div>
                      {d.warnungen.length > 0 && (
                        <div className="importwarnung" role="status">
                          <b>{anzahl(d.warnungen.length)} Warnungen</b>
                          {' · '}{d.warnungen.slice(0, 3).join(' · ')}
                          {d.warnungen.length > 3 && ' · …'}
                        </div>
                      )}
                      {d.uebersprungen > 0 && d.datenzeilen > 0
                        && (d.uebersprungen >= 10
                          || d.uebersprungen / d.datenzeilen >= 0.01) && (
                        <div className="importwarnung stark">
                          Von {anzahl(d.datenzeilen)} Zeilen wurden{' '}
                          {anzahl(d.uebersprungen)} übersprungen.
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(erg.ohne_archiv ?? []).length > 0 && (
              <div className="importbefund">
                <h3>
                  {erg.ohne_archiv.length === 1
                    ? 'Ein Berichtstag ohne Kassenarchiv'
                    : `${anzahl(erg.ohne_archiv.length)} Berichtstage ohne Kassenarchiv`}
                </h3>
                <ul>
                  {erg.ohne_archiv.map((o) => (
                    <li key={`${o.betrieb}-${o.tag}`}>
                      <b>{o.standort}</b> · {datumMittel(o.tag)}
                    </li>
                  ))}
                </ul>
                <p>
                  Diese Berichtstage haben noch kein Archiv. Gästezahl
                  und Tischbelegungen stehen, Umsatz und Rechnungszahl
                  nicht. Sobald das Archiv nachkommt, füllen sie sich von
                  selbst.
                </p>
              </div>
            )}

            {erg.neue_betriebe.length > 0 && (
              <p className="dezent">
                Neu angelegt: <b>{erg.neue_betriebe.join(', ')}</b>. Name
                und Anschrift kommen aus der Betriebsstätte im Archiv,
                das Dateimuster aus deren Ort — und beides steht so lange
                als „automatisch" da, bis jemand es angesehen hat.
              </p>
            )}

            {/* Eine Zahlart, die wir nicht kennen, laeuft als Sonstiges
                mit. Die Betraege stimmen, der Abgleich geht auf, die
                Barquote stimmt - falsch ist allein die Einordnung, und
                die weicht in keiner Zahl ab. Wer das nicht hier liest,
                liest es nirgends. */}
            {(erg.zahlarten_offen ?? []).length > 0 && (
              <div className="karte hervor">
                <h3>
                  {erg.zahlarten_offen.length === 1
                    ? 'Eine unbekannte Zahlart'
                    : `${anzahl(erg.zahlarten_offen.length)} unbekannte Zahlarten`}
                </h3>
                <ul>
                  {erg.zahlarten_offen.map((z) => <li key={z}><b>{z}</b></li>)}
                </ul>
                <p className="dezent">
                  Diesen Zahlarten-Typ kennt das System nicht. Die Zahlungen
                  sind vollständig eingelesen und in jeder Summe enthalten —
                  eingeordnet sind sie vorläufig als <b>Sonstiges</b>. Bitte
                  unter <b>Einrichtung</b> einmal sagen, was es ist: Karte,
                  Gutschein oder Sonstiges. Danach fasst kein Import die
                  Zuordnung mehr an.
                </p>
              </div>
            )}

            {/* Kein dezenter Absatz: Ohne Dateimuster bleiben die fuenf
                Berichtsarten dieses Standorts beim naechsten Import
                liegen, und das faellt sonst erst Wochen spaeter auf. */}
            {erg.ohne_muster.length > 0 && (
              <div className="karte hervor">
                <h3>
                  {erg.ohne_muster.length === 1
                    ? 'Ein Standort ohne Dateimuster'
                    : `${anzahl(erg.ohne_muster.length)} Standorte ohne Dateimuster`}
                </h3>
                <ul>
                  {erg.ohne_muster.map((o) => (
                    <li key={o.standort}>
                      <b>{o.standort}</b> — {o.grund}
                    </li>
                  ))}
                </ul>
                <p className="dezent">
                  Alle fünf Berichtsarten tragen den Standort
                  <b> nur im Dateinamen</b>. Ohne Muster bleiben sie beim
                  Import liegen — außer es gibt genau einen aktiven
                  Standort, dann gehen sie dorthin. Tragen Sie unter{' '}
                  <b>Standorte</b> ein
                  eindeutiges Stück aus dem echten Dateinamen ein —
                  mindestens vier Zeichen. Bei
                  „…_stuttgartabaccossteakhouse_quittungen_…" wäre das
                  etwa <span className="zahl">stuttgartabaccossteakhouse</span>.
                </p>
              </div>
            )}

            {erg.tage.length > 0 && (
              <p className="dezent">
                Geschäftstage: {erg.tage.join(', ')}
              </p>
            )}
          </div>
        )}
      </div>

      <OffeneGruppen gruppen={offen} fertig={offeneLaden} />
    </div>
  )
}
